/**
 * ---------------------------------------------------------------------------
 * PRODUCT PAGE INTERACTION TRACKING
 * ---------------------------------------------------------------------------
 *
 * Instruments the product detail page so GA4 can answer "how far down this page
 * do people actually get, what do they touch on the way, and what stops them
 * buying". It is the PDP half of the pair that starts with cart-tracking.js:
 * that file picks the shopper up at the cart, this one covers everything before
 * it.
 *
 * BASELINE INSTRUMENTATION, not an experiment. Nothing a shopper sees changes.
 * It is additive and never emits the GA4 standard ecommerce names (`view_item`,
 * `add_to_cart`, `select_item`), because Shopify's own Google channel pixel and
 * the GTM container already send those and a second source would corrupt the
 * existing ecommerce reports. Every event here is `ss_pdp_*`, which nothing else
 * on this storefront emits.
 *
 *
 * WHY THIS PAGE, AND WHY NOW
 * --------------------------
 * /products/beatbuddy-2 is the busiest landing page on the site: 23,716 landing
 * sessions and 44,005 page views in the 28 days to 2026-09-18, 84% of them
 * mobile. The item funnel over the same window reads
 *
 *     20,528 viewed  ->  1,229 added to cart  ->  527 checked out  ->  64 bought
 *
 * so roughly 94% of the people who look at it never add it, and half of those
 * who do never reach checkout. Nothing currently on the page can say why. GA4's
 * built-in `scroll` fires once, at 90%, and reports that about 9% of mobile
 * sessions get there - on a page with TWENTY-THREE sections that is a single bit
 * of information about a very long read.
 *
 * This file runs on every product page, not just that one, and carries
 * `product_handle` on every event. The other product pages are the site's other
 * landing pages, so instrumenting them all now means the next test does not have
 * to start by waiting for data.
 *
 *
 * WHAT THE BB2 PAGE IS ACTUALLY MADE OF
 * -------------------------------------
 * Five findings from reading the live page (2026-09-19) shaped the design.
 *
 * 1. THE "PRODUCT TYPE" PICKER IS NOT A VARIANT PICKER. A custom liquid block
 *    renders three radios named `product-type-option` - BeatBuddy 2,
 *    + SuperSwitch, + SuperSwitch + Digital Content - that look exactly like the
 *    real plug-type swatches below them but are not Shopify options. Inline
 *    scripts swap which price block is visible ($499 / $648 / $810.94) and a
 *    separate script maps the choice to a list of variant ids. Two things
 *    follow: the price a shopper reads and the variant the form holds can
 *    disagree, and the choice is invisible to every standard analytics
 *    integration on the page. It is captured here as `pdp_bundle_choice`.
 *
 * 2. THE MAIN ADD TO CART BUTTON IS HIJACKED. An inline script binds it with
 *    `e.preventDefault(); e.stopPropagation();` and posts `/cart/add.js` itself
 *    so it can add the bundle's extra items. Consequences:
 *
 *      - A failed add is SILENT. The catch block only does console.log, the
 *        button re-enables, and the shopper sees nothing happen at all. This is
 *        the most likely "user having trouble" case on the page and nothing
 *        measures it today, which is why `atc_result` exists.
 *      - stopPropagation kills bubble-phase listeners on any ancestor. Every
 *        listener in this file is therefore delegated from `document` in the
 *        CAPTURE phase, which runs before the target's own handler.
 *
 * 3. THE STICKY BAR USES A DIFFERENT FORM. `<product-quick-add>` renders its own
 *    `quick-buy-form-...` with its own variant picker, and the bundle script
 *    binds ONLY the main `product-form-...`. So the sticky bar submits natively
 *    and adds the base pedal, ignoring a bundle the shopper picked further up.
 *    `atc_surface` plus `pdp_bundle_choice` makes that mismatch countable
 *    instead of theoretical.
 *
 * 4. SHOP PAY IGNORES THE BUNDLE ENTIRELY. `<shopify-accelerated-checkout>`
 *    posts the form's variant id straight to checkout, so a bundle shopper who
 *    takes that route buys the $499 pedal. Tracked as `atc_surface:
 *    accelerated`.
 *
 * 5. THE PAGE IS 23 SECTIONS LONG and the interesting ones are at the bottom:
 *    the comparison table at 15, tech specs at 18, reviews at 21. Section-level
 *    view events are the whole point of this file - a per-section drop-off curve
 *    is what turns "the page is too long" from an opinion into a number, and it
 *    names the section to cut.
 *
 *
 * TRANSPORT
 * ---------
 * Identical to cart-tracking.js, and deliberately so. gtag direct to GA4 on
 * G-P1B5YHZ105 is the primary path, because the GTM container on this storefront
 * (GTM-P7KQTLKR) belongs to Max Connect Marketing and we have no edit access to
 * it - a dataLayer push alone would go nowhere. `send_page_view: false` so we
 * never add a second page-view source, and `send_to` on every event so nothing
 * leaks onto the AW-976948441 Google Ads destination that layout/theme.liquid
 * configures.
 *
 * The gtag config is shared with cart-tracking.js through `SS.ga4Ready`, so
 * whichever file loads first configures the stream and the other reuses it. On a
 * product page both files are running.
 *
 *
 * PRIVACY
 * -------
 * No field value is ever captured. `describeElement` refuses to read text from
 * `input`, `textarea` and `select`, so nothing a shopper types can leave the
 * page. The only values recorded are choices from fixed sets (bundle, plug type)
 * and numbers.
 *
 *
 * Written 2026-09-19. Source of truth is this repo file.
 */
(function () {
    'use strict';

    /* ---------------------------------------------------------------- config */

    var CONFIG = {
        /* GA4 measurement ID for the singularsound.com web data stream. Set to
         * '' and this falls back to dataLayer-only, which on this storefront
         * means the events go nowhere - see TRANSPORT above. */
        measurementId: 'G-P1B5YHZ105',

        /* Seconds a visible page can sit with no interaction before it counts as
         * hesitation. Longer than the cart's 20s on purpose: reading a product
         * page for half a minute is normal, stalling in a cart is not. */
        hesitationSeconds: 40,

        /* Fires at most once per page view, so a shopper who reads for ten
         * minutes produces one hesitation event, not fifteen. */
        hesitationOncePerPage: true,

        /* Fraction of a section that must be on screen before it counts as seen,
         * and how long it must stay there. The dwell requirement is what stops a
         * fast scroll to the reviews from reporting all 23 sections as read. */
        sectionVisibleRatio: 0.4,
        sectionDwellMs: 800,

        /* Scroll milestones, sent into the existing `scroll_depth_percent`
         * dimension that Exp-8 registered. GA4's own `scroll` event only fires
         * at 90%, which is close to useless on a page this long. */
        scrollMilestones: [25, 50, 75, 90],

        /* How long to wait for the cart to actually change after an add-to-cart
         * click before calling it a failure. The bundle script's fetch is
         * typically well under a second; 5s is generous enough that a slow
         * connection is not reported as a broken button. */
        atcConfirmMs: 5000,

        /* Append ?ss_track_debug=1 to any URL to log every event to the console
         * without changing what is sent. Persists for the tab, and is the same
         * switch cart-tracking.js uses. */
        debug: false
    };

    try {
        if (/[?&]ss_track_debug=1/.test(window.location.search)) {
            window.sessionStorage.setItem('ss_track_debug', '1');
        }
        CONFIG.debug = window.sessionStorage.getItem('ss_track_debug') === '1';
    } catch (e) {}

    /* Namespaced under window.SS like the Convert global JS and cart-tracking.js,
     * so nothing here can collide with the theme, ShipInsure, PageFly, Rebuy or
     * the other apps. */
    var SS = window.SS = window.SS || {};
    if (SS.pdpTracking) return;              /* already running, do not double-bind */

    /* Product pages only. Everything below binds document-level listeners and
     * there is no reason to carry that on a collection or a blog post. */
    function isProductPage() {
        try {
            if (window.themeVariables && window.themeVariables.settings &&
                window.themeVariables.settings.pageType) {
                return window.themeVariables.settings.pageType === 'product';
            }
        } catch (e) {}
        return /^\/products\//.test(location.pathname);
    }

    if (!isProductPage()) return;

    /* ------------------------------------------------------------- transport */

    /* Shared with cart-tracking.js through SS.ga4Ready. Both files run on a
     * product page and both would otherwise configure the same stream. */
    function ensureGa4() {
        if (SS.ga4Ready) return true;
        if (!/^G-[A-Z0-9]+$/i.test(CONFIG.measurementId)) return false;

        try {
            if (typeof window.gtag !== 'function') {
                window.dataLayer = window.dataLayer || [];
                window.gtag = function () { window.dataLayer.push(arguments); };

                var script = document.createElement('script');
                script.async = true;
                script.src = 'https://www.googletagmanager.com/gtag/js?id=' + CONFIG.measurementId;
                document.head.appendChild(script);

                window.gtag('js', new Date());
            }

            window.gtag('config', CONFIG.measurementId, { send_page_view: false });
            SS.ga4Ready = true;
        } catch (e) {}

        return !!SS.ga4Ready;
    }

    function trackEvent(name, params) {
        var payload = basePayload();

        for (var key in params) {
            if (Object.prototype.hasOwnProperty.call(params, key) && params[key] !== undefined && params[key] !== null) {
                payload[key] = params[key];
            }
        }

        /* Secondary, and inert until someone adds an ^ss_ trigger to the GTM
         * container. Kept for GTM Preview and console debugging. */
        try {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push(Object.assign({ event: name }, payload));
        } catch (e) {}

        /* Primary. */
        try {
            if (ensureGa4()) {
                window.gtag('event', name, Object.assign({
                    send_to: CONFIG.measurementId,
                    /* Beacon so the exit event survives the unload it reports. */
                    transport_type: 'beacon'
                }, payload));
            }
        } catch (e) {}

        if (CONFIG.debug) {
            try {
                console.log('%c[ss-pdp-track]', 'color:#f0c417', name,
                    SS.ga4Ready ? '-> ' + CONFIG.measurementId : '-> dataLayer only', payload);
            } catch (e) {}
        }
    }

    function basePayload() {
        return {
            page_type: 'product',
            page_path: location.pathname,
            product_handle: state.handle,
            viewport_width: window.innerWidth,
            viewport_group: window.innerWidth < 700 ? 'mobile' : (window.innerWidth < 1000 ? 'tablet' : 'desktop')
        };
    }

    /* --------------------------------------------------------- product state */

    var state = {
        handle: productHandle(),
        startedAt: Date.now(),
        actionCount: 0,
        lastAction: 'none',
        lastActionAt: 0,
        firstActionSeconds: null,
        reachedAtc: false,
        atcSurface: null,
        maxScroll: 0,
        milestonesSent: {},
        sectionsSeen: 0,
        deepestSectionIndex: 0,
        deepestSection: null,
        currentSection: null,
        hesitationSent: false,
        hesitationTimer: null,
        exitTo: null,
        exitFired: false
    };

    function productHandle() {
        var match = location.pathname.match(/\/products\/([^/?#]+)/);
        if (match) return decodeURIComponent(match[1]).slice(0, 100);

        var picker = document.querySelector('variant-picker[handle]');
        return (picker && picker.getAttribute('handle')) || 'unknown';
    }

    function seconds(from) {
        return Math.round((Date.now() - from) / 1000);
    }

    /* ------------------------------------------------------- selection state */

    /* The two choices a shopper makes on this page, both read live rather than
     * stored, so a change made by any of the page's own inline scripts is picked
     * up rather than shadowed.
     *
     * `product-type-option` is the fake picker described at the top of this file
     * - it is not a Shopify option and appears in no other data source.
     * `option1` is the real one (plug type: US / UK / EU / AU). */
    function bundleChoice() {
        var checked = document.querySelector('input[name="product-type-option"]:checked');
        if (!checked) return undefined;

        var text = String(checked.value || labelTextFor(checked) || '').toLowerCase();
        if (text.indexOf('digital') !== -1) return 'superswitch_digital';
        if (text.indexOf('superswitch') !== -1) return 'superswitch';
        return 'base';
    }

    function variantChoice() {
        var checked = document.querySelector('input[name="option1"]:checked');
        var value = checked ? (checked.value || labelTextFor(checked)) : null;

        /* Falls back to the native <select>, which is what renders when the
         * theme is set to a dropdown rather than swatches. */
        if (!value) {
            var select = document.querySelector('select[name="id"]');
            var option = select && select.options[select.selectedIndex];
            value = option && option.textContent;
        }

        return value ? String(value).replace(/\s+/g, ' ').trim().slice(0, 50) : undefined;
    }

    function labelTextFor(input) {
        if (!input || !input.id) return '';
        var label = document.querySelector('label[for="' + cssEscape(input.id) + '"]');
        return label ? (label.textContent || '').replace(/\s+/g, ' ').trim() : '';
    }

    function cssEscape(value) {
        if (window.CSS && typeof window.CSS.escape === 'function') return window.CSS.escape(value);
        return String(value).replace(/[^\w-]/g, '\\$&');
    }

    function selectionContext() {
        return {
            pdp_bundle_choice: bundleChoice(),
            pdp_variant_choice: variantChoice()
        };
    }

    function pagePayload() {
        return Object.assign({
            dwell_seconds: seconds(state.startedAt),
            action_count: state.actionCount,
            last_action: state.lastAction,
            sections_seen: state.sectionsSeen,
            max_scroll_percent: state.maxScroll
        }, selectionContext());
    }

    function noteAction(action) {
        state.actionCount++;
        state.lastAction = action;
        state.lastActionAt = Date.now();

        if (state.firstActionSeconds === null) {
            state.firstActionSeconds = seconds(state.startedAt);
        }

        scheduleHesitation();
    }

    /* ----------------------------------------------------------- section map */

    /* One entry per `#shopify-section-*` on the page, in document order.
     *
     * The label is deliberately built from the section's own heading rather than
     * its id. Shopify section ids carry a template-scoped prefix
     * (`template--23020469256436__rich_text_qdpDT8`) that changes whenever the
     * template is duplicated - which is exactly what stranded the hardcoded
     * selectors in the first version of the Exp-3 quick links. A heading is
     * stable, readable in a GA4 report, and if the copy changes then the label
     * changing with it is a true statement about the page.
     *
     * The index prefix keeps the values sorting in page order, which is what
     * makes the drop-off curve readable without a lookup table. */
    var sections = [];

    function buildSectionMap() {
        sections = [];

        /* Scoped to <main>, and theme SECTION GROUPS are skipped.
         *
         * Checked against the live page: the document carries 30 top-level
         * `#shopify-section-*` elements, only 23 of which are the product page.
         * The other seven are the header, the footer, the cart drawer, the
         * search drawer, the privacy banner and two utility blocks. Counting
         * them would be wrong twice over - they would take index numbers away
         * from the real sections, so `18-tech-specs` would silently become
         * `24-tech-specs` the day the theme adds another drawer, and
         * `section_count` would report a 30-section page.
         *
         * Shopify names the two kinds differently and that is the handle used
         * here: a template section is `template--<id>__name`, a section group
         * is `sections--<id>__name`. */
        var root = document.querySelector('main') || document.body || document;
        var nodes = root.querySelectorAll('[id^="shopify-section-"]');

        for (var i = 0; i < nodes.length; i++) {
            var node = nodes[i];

            if (node.id.indexOf('shopify-section-sections--') === 0) continue;

            /* Nested sections (app blocks render their own) would double-count
             * the parent's area and inflate sections_seen. */
            if (node.parentElement && node.parentElement.closest('[id^="shopify-section-"]')) continue;

            sections.push({
                index: sections.length + 1,
                el: node,
                label: null,          /* resolved lazily, once the section is seen */
                seen: false
            });
        }

        /* PageFly and app-built pages do not render Shopify sections. Falling
         * back to nothing is correct: scroll milestones still work, and a
         * section funnel on a page with no sections would be a fiction. */
        return sections.length;
    }

    function sectionLabel(entry) {
        if (entry.label) return entry.label;

        var suffix = (entry.el.id || '').indexOf('__') !== -1
            ? entry.el.id.split('__').pop()
            : (entry.el.id || '').replace('shopify-section-', '');

        /* The buy column is named by its section id, never by a heading found
         * inside it. It has no heading of its own - the BeatBuddy 2 product
         * title is an image block, a PNG, and the page carries no <h1> anywhere
         * - so the first heading in it belongs to whichever app happens to be
         * rendering there. That labelled the whole buy column `01-buy-it-with`,
         * after Rebuy's upsell. `01-main` is stable and does not move when an
         * app is added or removed.
         *
         * The missing <h1> is an SEO problem in its own right and is flagged in
         * the spec; it is not this file's job to fix it. */
        if (suffix === 'main') {
            entry.label = (entry.index < 10 ? '0' : '') + entry.index + '-main';
            return entry.label;
        }

        /* Tried in order of how well each names a section, not merged into one
         * selector - `querySelector` with a list returns whichever comes first
         * in the DOM rather than the best match.
         *
         * A <legend> is deliberately NOT in the chain. It labels a form field,
         * not a section, and on the live buy column it is the variant picker's
         * "Product type:", which labelled the whole main section
         * `01-product-type-beatbuddy-2`. Falling through to the id suffix gives
         * `01-main`, which is both stable and honest.
         *
         * Worth knowing why the buy column has no heading to find: the
         * BeatBuddy 2 page has no <h1> anywhere on it. The product title is an
         * image block, a PNG. That is an SEO problem in its own right and is
         * flagged in the spec. */
        var heading = entry.el.querySelector('h1') ||
            entry.el.querySelector('h2') ||
            entry.el.querySelector('h3') ||
            /* The Impact theme styles its section headings with a scale class on
             * a <p>: `<p class="h0">Brief Intro</p>`, `<p class="h1">New for
             * BeatBuddy 2</p>`. Visually a heading, semantically a paragraph.
             * Without this line most of the sections on the page fall back to
             * their id suffix and the funnel reads `03-rich-text-wabtqj` where
             * it could read `03-brief-intro`. */
            entry.el.querySelector('.h0, .h1, .h2, .h3, .h4') ||
            entry.el.querySelector('[class*="heading"]');

        var text = heading ? (heading.textContent || '') : '';

        /* No heading: fall back to the id suffix, which at least names the
         * section type (`media_with_text_QDxUcr`, `video_EbnCwD`). */
        if (!text.replace(/\s+/g, '')) text = suffix;

        var slug = String(text)
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '')
            .slice(0, 40) || 'unnamed';

        entry.label = (entry.index < 10 ? '0' : '') + entry.index + '-' + slug;
        return entry.label;
    }

    function sectionFor(el) {
        if (!el || !el.closest) return null;

        var node = el.closest('[id^="shopify-section-"]');
        if (!node) return null;

        for (var i = 0; i < sections.length; i++) {
            if (sections[i].el === node || sections[i].el.contains(node)) return sections[i];
        }

        return null;
    }

    /* Which section the shopper was looking at, for the hesitation and exit
     * events. Answers "where were they when they gave up". */
    function currentSectionLabel() {
        return state.currentSection ? sectionLabel(state.currentSection) : undefined;
    }

    var pendingSectionTimers = {};

    function queueSeen(record, observer) {
        if (record.seen || pendingSectionTimers[record.index]) return;

        pendingSectionTimers[record.index] = window.setTimeout(function () {
            delete pendingSectionTimers[record.index];
            markSeen(record, observer);
        }, CONFIG.sectionDwellMs);
    }

    function markSeen(record, observer) {
        if (record.seen) return;
        record.seen = true;

        state.sectionsSeen++;

        if (record.index > state.deepestSectionIndex) {
            state.deepestSectionIndex = record.index;
            state.deepestSection = sectionLabel(record);
        }

        trackEvent('ss_pdp_section_view', {
            pdp_section: sectionLabel(record),
            section_index: record.index,
            sections_seen: state.sectionsSeen,
            seconds_to_section: seconds(state.startedAt),
            dwell_seconds: seconds(state.startedAt),
            action_count: state.actionCount,
            max_scroll_percent: state.maxScroll
        });

        try { if (observer) observer.unobserve(record.el); } catch (e) {}
    }

    function recordFor(target) {
        for (var i = 0; i < sections.length; i++) {
            if (sections[i].el === target) return sections[i];
        }
        return null;
    }

    function watchSections() {
        if (!buildSectionMap()) return;
        if (typeof window.IntersectionObserver !== 'function') return;

        /* Two observers, because one threshold cannot cover both shapes of
         * section on this page.
         *
         *   ratio 0.4    a normal section, where "40% of it is on screen" is a
         *                fair definition of having looked at it
         *   band         a section TALLER than the viewport can never reach 0.4
         *                visible, and on a phone most of the full-bleed media
         *                sections here are. The second observer fires when any
         *                part of it occupies the middle 30% of the screen
         *
         * markSeen is guarded by record.seen, so a section that satisfies both
         * is still counted once. */
        var ratioObserver = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
                var record = recordFor(entries[i].target);
                if (!record) continue;

                if (entries[i].isIntersecting) {
                    state.currentSection = record;
                    queueSeen(record, ratioObserver);
                } else if (pendingSectionTimers[record.index]) {
                    window.clearTimeout(pendingSectionTimers[record.index]);
                    delete pendingSectionTimers[record.index];
                }
            }
        }, { threshold: CONFIG.sectionVisibleRatio });

        var bandObserver = new IntersectionObserver(function (entries) {
            for (var i = 0; i < entries.length; i++) {
                var record = recordFor(entries[i].target);
                if (!record || !entries[i].isIntersecting) continue;

                state.currentSection = record;
                queueSeen(record, ratioObserver);
            }
        }, { threshold: 0, rootMargin: '-35% 0px -35% 0px' });

        for (var i = 0; i < sections.length; i++) {
            try {
                ratioObserver.observe(sections[i].el);
                bandObserver.observe(sections[i].el);
            } catch (e) {}
        }
    }

    /* ---------------------------------------------------------------- scroll */

    function onScroll() {
        var doc = document.documentElement;
        var height = Math.max(doc.scrollHeight || 0, document.body ? document.body.scrollHeight : 0);

        if (!height || height <= window.innerHeight) return;

        var travelled = (window.pageYOffset || doc.scrollTop || 0) + window.innerHeight;
        var percent = Math.min(100, Math.round((travelled / height) * 100));

        if (percent <= state.maxScroll) return;
        state.maxScroll = percent;

        for (var i = 0; i < CONFIG.scrollMilestones.length; i++) {
            var milestone = CONFIG.scrollMilestones[i];

            if (percent >= milestone && !state.milestonesSent[milestone]) {
                state.milestonesSent[milestone] = true;

                trackEvent('ss_pdp_scroll', Object.assign(pagePayload(), {
                    /* Reuses the dimension Exp-8 registered rather than spending
                     * another slot on the same idea. */
                    scroll_depth_percent: String(milestone),
                    seconds_to_scroll: seconds(state.startedAt),
                    pdp_section: currentSectionLabel()
                }));
            }
        }
    }

    /* -------------------------------------------------- element description */

    /* A compact descriptor of what was clicked. Same rules as cart-tracking.js:
     * never reads value or text from a form field, capped well under GA4's
     * 100-character parameter limit. */
    function describeElement(el) {
        if (!el || !el.tagName) return 'unknown';

        var tag = el.tagName.toLowerCase();
        var parts = [tag];

        var label = el.getAttribute && (el.getAttribute('aria-label') || el.getAttribute('name') || el.getAttribute('data-testid'));
        if (label) parts.push('@' + String(label).slice(0, 30));

        var cls = '';
        if (typeof el.className === 'string') cls = el.className;
        else if (el.className && el.className.baseVal) cls = el.className.baseVal;   /* SVG */

        cls = cls.trim().split(/\s+/).filter(Boolean).slice(0, 2).join('.');
        if (cls) parts.push('.' + cls.slice(0, 40));

        var readsText = tag !== 'input' && tag !== 'textarea' && tag !== 'select' &&
            !(el.closest && el.closest('input, textarea, select'));

        if (readsText) {
            var text = (el.textContent || '').replace(/\s+/g, ' ').trim();
            if (text) parts.push('"' + text.slice(0, 40) + '"');
        }

        return parts.join(' ').slice(0, 90);
    }

    function actionableIn(path) {
        var interactive = 'a,button,summary,label,input,select,textarea,[role="button"],[role="switch"],[role="checkbox"],[aria-controls]';

        for (var i = 0; i < path.length; i++) {
            var node = path[i];
            if (!node || !node.tagName || !node.matches) continue;
            try { if (node.matches(interactive)) return node; } catch (e) {}
        }

        return path[0] && path[0].tagName ? path[0] : null;
    }

    function matchesAny(path, selector) {
        for (var i = 0; i < path.length; i++) {
            var node = path[i];
            if (!node || !node.matches) continue;
            try { if (node.matches(selector)) return true; } catch (e) {}
        }
        return false;
    }

    function textOf(el) {
        if (!el) return '';
        var tag = el.tagName ? el.tagName.toLowerCase() : '';
        if (tag === 'input' || tag === 'textarea' || tag === 'select') return '';
        return (el.textContent || '').replace(/\s+/g, ' ').trim();
    }

    /* A click on a <label> never carries its input in the composed path, so the
     * swatches - which are all a `.sr-only` input plus a visible label - would
     * fall through to `other` without this. */
    function labelTargets(path, inputName) {
        for (var i = 0; i < path.length; i++) {
            var node = path[i];
            if (!node || node.tagName !== 'LABEL') continue;

            var forId = node.getAttribute('for');

            if (!forId) {
                if (node.querySelector && node.querySelector('input[name="' + inputName + '"]')) return true;
                continue;
            }

            var input = document.getElementById(forId);
            if (input && input.getAttribute('name') === inputName) return true;
        }
        return false;
    }

    /* --------------------------------------------------------- classification */

    /* Ordered. First match wins, so the specific rules sit above the generic
     * ones. Each test receives the composed path and the actionable element.
     *
     * ORDERING TRAPS, both of which invert a headline number if reordered:
     *
     *   - `add_to_cart_sticky` MUST sit above `add_to_cart`. The sticky bar's
     *     button is also a submit inside a `buy-buttons`, so the generic rule
     *     matches it too. Reversed, the two surfaces pool and the
     *     bundle-versus-sticky mismatch this file exists to measure disappears.
     *   - `bundle_option` MUST sit above `variant_option`. Both are `.sr-only`
     *     radios with a `.block-swatch` label and they are indistinguishable in
     *     the DOM apart from the input name; the fake one is the interesting
     *     one, and pooling them hides it inside the real variant picker. */
    var ACTIONS = [
        {
            action: 'add_to_cart_sticky',
            test: function (path) {
                return matchesAny(path, 'product-quick-add, .product-quick-add, sticky-add-to-cart, [class*="sticky-atc"]') &&
                    matchesAny(path, 'button[type="submit"], buy-buttons button');
            }
        },
        {
            action: 'buy_now',
            test: function (path) {
                return matchesAny(path, 'shopify-accelerated-checkout, .shopify-payment-button, shop-pay-wallet-button, [data-shopify="payment-button"], .additional-checkout-buttons');
            }
        },
        {
            action: 'add_to_cart',
            test: function (path, el) {
                if (matchesAny(path, 'buy-buttons button[type="submit"], form[action*="/cart/add"] button[type="submit"], button[name="add"]')) return true;
                return !!(el && el.tagName === 'BUTTON' && /^(add to cart|add to bag|pre.?order)/i.test(textOf(el)));
            }
        },
        {
            action: 'bundle_option',
            test: function (path) {
                return matchesAny(path, '[name="product-type-option"]') || labelTargets(path, 'product-type-option');
            }
        },
        {
            action: 'variant_option',
            test: function (path) {
                return matchesAny(path, 'variant-picker input, variant-picker label, variant-picker select, [name="option1"], [name="option2"], [name="option3"], select[name="id"]') ||
                    labelTargets(path, 'option1');
            }
        },
        {
            action: 'quantity_stepper',
            test: function (path) {
                return matchesAny(path, '.quantity-selector__button, quantity-selector button');
            }
        },
        {
            action: 'quantity_field',
            test: function (path) {
                /* The label is part of the control: tapping "Quantity:" focuses
                 * the field, and on the live page it was the only visible thing
                 * in the buy column still falling through to `other`. */
                return matchesAny(path, 'input[name="quantity"], .quantity-selector__input') ||
                    labelTargets(path, 'quantity');
            }
        },
        {
            action: 'gallery_zoom',
            test: function (path) {
                return matchesAny(path, '[is="product-zoom-button"], product-zoom-button, .product-gallery__cursor, custom-cursor');
            }
        },
        {
            action: 'gallery_media',
            test: function (path) {
                return matchesAny(path, '.product-gallery__thumbnail, product-gallery, media-carousel, page-dots');
            }
        },
        {
            action: 'video_play',
            test: function (path, el) {
                if (matchesAny(path, 'video, deferred-media, .video-wrapper, [class*="video"] button, [is="play-button"]')) return true;
                return !!(el && el.getAttribute && /play/i.test(el.getAttribute('aria-label') || ''));
            }
        },
        {
            action: 'quick_link',
            test: function (path) {
                /* The Exp-3 "What's New / Compare / Tech Specs / Reviews" jump
                 * links, which shipped into the theme after that test won. */
                return matchesAny(path, '.bb2-quick-links a, [class*="quick-link"] a, [data-quick-link]');
            }
        },
        {
            action: 'review_link',
            test: function (path, el) {
                if (matchesAny(path, 'a.rating, .rating a, a[href*="#shopify-product-reviews"], a[href*="#judgeme"]')) return true;
                var text = textOf(el);
                return !!(text && text.length < 30 && /reviews?$/i.test(text));
            }
        },
        {
            action: 'review_widget',
            test: function (path) {
                return matchesAny(path, '.jdgm-widget, [class^="jdgm"], [class*=" jdgm"], .yotpo, [class*="review-widget"]');
            }
        },
        {
            action: 'recommendation',
            test: function (path) {
                /* Rebuy's "Buy It With" inside the buy column, plus the "You May
                 * Also Like" collection at the foot of the page. */
                return matchesAny(path, 'rebuy-products, [class*="rebuy"], .horizontal-product, product-card, .product-card, [class*="featured-collection"] a');
            }
        },
        {
            action: 'accordion',
            test: function (path) {
                return matchesAny(path, 'summary, accordion-details, [class*="accordion"] summary, [aria-expanded]');
            }
        },
        {
            action: 'download_link',
            test: function (path, el) {
                var href = el && el.getAttribute && el.getAttribute('href');
                if (href && /\.(pdf|zip|exe|dmg|txt|csv)(\?|$)/i.test(href)) return true;
                return matchesAny(path, 'a[download]');
            }
        },
        {
            action: 'payment_terms',
            test: function (path) {
                return matchesAny(path, 'payment-terms, shopify-payment-terms, [class*="payment-terms"]');
            }
        },
        {
            action: 'breadcrumb',
            test: function (path) {
                return matchesAny(path, '.breadcrumb a, .breadcrumb__link, nav[aria-label="Breadcrumb"] a');
            }
        },
        {
            action: 'policy_link',
            test: function (path, el) {
                var href = el && el.getAttribute && el.getAttribute('href');
                return !!(href && /\/policies\/|\/pages\/(shipping|returns|warranty|faq)/i.test(href));
            }
        },
        {
            action: 'product_link',
            test: function (path, el) {
                var href = el && el.getAttribute && el.getAttribute('href');
                return !!(href && href.indexOf('/products/') !== -1);
            }
        },
        {
            action: 'header_nav',
            test: function (path) {
                /* Leaving upward. Counted so the exit report can tell a shopper
                 * who went looking for something else from one who just left. */
                return matchesAny(path, 'header a, header button, [class*="header__"] a, nav a');
            }
        },
        {
            action: 'footer_link',
            test: function (path) {
                return matchesAny(path, 'footer a, footer button');
            }
        }
    ];

    function classify(path, el) {
        for (var i = 0; i < ACTIONS.length; i++) {
            try {
                if (ACTIONS[i].test(path, el)) return ACTIONS[i].action;
            } catch (e) {}
        }
        return 'other';
    }

    function classifyDestination(href) {
        var url;
        try { url = new URL(href, location.origin); } catch (e) { return 'unknown'; }

        if (url.origin !== location.origin) return 'external';

        var p = url.pathname;
        if (p === '/') return 'home';
        if (p.indexOf('/cart') === 0) return 'cart';
        if (p.indexOf('/checkout') === 0) return 'checkout';
        if (p.indexOf('/products/') === 0) return p === location.pathname ? 'same_product' : 'product';
        if (p.indexOf('/collections/') === 0) return 'collection';
        if (p.indexOf('/policies/') === 0) return 'policy';
        if (p.indexOf('/blogs/') === 0) return 'blog';
        if (p.indexOf('/pages/') === 0) return 'page';
        if (p.indexOf('/account') === 0) return 'account';
        return 'other';
    }

    /* ----------------------------------------------------------- click stream */

    document.addEventListener('click', function (event) {
        var path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
        var el = actionableIn(path);

        /* Destination capture first, so ss_pdp_exit knows where they went even
         * when the click itself is an ordinary link. */
        var href = el && el.getAttribute && el.getAttribute('href');
        var destination = null;

        if (href && href.charAt(0) !== '#') {
            destination = classifyDestination(href);
            if (destination !== 'same_product') state.exitTo = destination;
        }

        var action = classify(path, el);
        var section = sectionFor(el);

        noteAction(action);

        trackEvent('ss_pdp_click', Object.assign(pagePayload(), {
            pdp_action: action,
            pdp_element: describeElement(el),
            pdp_section: section ? sectionLabel(section) : undefined,
            section_index: section ? section.index : undefined,
            click_destination: destination || undefined
        }));

        /* Add to cart gets its own event as well, for the same reason checkout
         * does in the cart file: it is the funnel step, and a funnel should not
         * have to filter on a parameter to find its own step. */
        if (action === 'add_to_cart' || action === 'add_to_cart_sticky' || action === 'buy_now') {
            recordAddToCart(
                action === 'add_to_cart_sticky' ? 'sticky' : action === 'buy_now' ? 'accelerated' : 'main',
                el
            );
        }
    }, true);

    /* --------------------------------------------------------- option changes */

    /* On `change` rather than the click, so a keyboard selection and the native
     * <select> are both caught. The click stream already counts the tap; this is
     * the event that records what the page ended up SET TO, which is the value
     * the add-to-cart is judged against. */
    document.addEventListener('change', function (event) {
        var el = event.target;
        if (!el || !el.getAttribute) return;

        var name = el.getAttribute('name');
        var option;

        if (name === 'product-type-option') option = 'bundle';
        else if (name === 'option1' || name === 'option2' || name === 'option3') option = 'variant';
        else if (name === 'id' && el.tagName === 'SELECT') option = 'variant';
        else if (name === 'quantity') option = 'quantity';
        else return;

        noteAction(option + '_change');

        var value;
        if (option !== 'quantity') {
            value = el.tagName === 'SELECT'
                ? (el.options[el.selectedIndex] || {}).textContent
                : (el.value || labelTextFor(el));
        }

        trackEvent('ss_pdp_option_change', Object.assign(pagePayload(), {
            pdp_action: option === 'bundle' ? 'bundle_option' : option === 'variant' ? 'variant_option' : 'quantity_field',
            /* Quantity is a number, not a choice, so it goes out as a metric and
             * never as a text value - the field is free text on this theme and
             * its contents must not leave the page. */
            qty_to: option === 'quantity' ? (parseInt(el.value, 10) || undefined) : undefined,
            option_value: value ? String(value).replace(/\s+/g, ' ').trim().slice(0, 50) : undefined,
            pdp_section: currentSectionLabel()
        }));
    }, true);

    document.addEventListener('focusin', function (event) {
        var el = event.target;
        if (el && el.getAttribute && el.getAttribute('name') === 'quantity') {
            noteAction('quantity_field');
        }
    }, true);

    /* ------------------------------------------------------------ add to cart */

    /* THE POINT OF THIS SECTION. The main button is hijacked by an inline script
     * that posts /cart/add.js and swallows its own errors (finding 2 at the top
     * of this file), so a click is NOT evidence that anything was added. The only
     * honest way to know is to look at the cart afterwards.
     *
     * `atc_result` therefore reports what happened, not what was clicked:
     *
     *   clicked             the intent. Always sent, immediately
     *   success             the cart's item count went up
     *   no_change           it did not, within atcConfirmMs. Either the add
     *                       failed silently or the button did nothing at all
     *   unknown             /cart.js could not be read, or there was no
     *                       before-value to compare against, so no claim is made
     *   left_for_checkout   Shop Pay, which never touches the cart
     *
     * `buy_now` is excluded from confirmation on purpose: the accelerated button
     * leaves for checkout without adding to the cart, so "the cart did not
     * change" is the correct outcome there and would otherwise read as a
     * failure. */
    var cartCount = null;

    function readCart(done) {
        try {
            var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

            fetch(root + 'cart.js', { credentials: 'same-origin' })
                .then(function (r) { return r.json(); })
                .then(function (json) { done(json && typeof json.item_count === 'number' ? json : null); })
                .catch(function () { done(null); });
        } catch (e) {
            done(null);
        }
    }

    /* Seeded once on load so there is a before-value to compare against, and kept
     * current from the theme's own cart:change event. */
    document.addEventListener('cart:change', function (event) {
        try {
            if (event.detail && event.detail.cart && typeof event.detail.cart.item_count === 'number') {
                cartCount = event.detail.cart.item_count;
            }
        } catch (e) {}
    });

    function recordAddToCart(surface, el) {
        var before = cartCount;

        var payload = Object.assign(pagePayload(), {
            atc_surface: surface,
            pdp_element: describeElement(el),
            seconds_to_atc: seconds(state.startedAt),
            pdp_section: currentSectionLabel()
        });

        if (!state.reachedAtc) {
            /* First route taken wins, so a shopper who taps the main button and
             * then the sticky one is still counted as `main`. */
            state.reachedAtc = true;
            state.atcSurface = surface;
        }

        if (surface === 'accelerated') {
            state.exitTo = 'checkout';
            trackEvent('ss_pdp_add_to_cart', Object.assign(payload, { atc_result: 'left_for_checkout' }));
            return;
        }

        /* Two events rather than one delayed event: a shopper whose add fails
         * often leaves straight away, and a single deferred hit would die with
         * the page and take the intent with it. Count intent on
         * ss_pdp_add_to_cart and outcome on ss_pdp_add_to_cart_result. Never add
         * the two together. */
        trackEvent('ss_pdp_add_to_cart', Object.assign({}, payload, { atc_result: 'clicked' }));

        window.setTimeout(function () {
            readCart(function (cart) {
                var result;

                /* No baseline means no claim. The obvious shortcut - "the cart
                 * has something in it, so the add worked" - is wrong for the
                 * majority of shoppers on this store, who reach a product page
                 * with items already in the cart and would every one of them be
                 * reported as a success whatever the button did. `unknown` is
                 * rare in practice: the baseline is fetched on page load and
                 * this runs atcConfirmMs after a click. */
                if (!cart) result = 'unknown';
                else if (before === null || before === undefined) result = 'unknown';
                else result = cart.item_count > before ? 'success' : 'no_change';

                if (cart) cartCount = cart.item_count;

                trackEvent('ss_pdp_add_to_cart_result', Object.assign({}, payload, {
                    atc_result: result,
                    cart_item_count: cart ? cart.item_count : undefined,
                    cart_value: cart && typeof cart.total_price === 'number' ? cart.total_price / 100 : undefined
                }));
            });
        }, CONFIG.atcConfirmMs);
    }

    /* ------------------------------------------------------------ hesitation */

    function scheduleHesitation() {
        if (state.hesitationTimer) window.clearTimeout(state.hesitationTimer);
        if (CONFIG.hesitationOncePerPage && state.hesitationSent) return;

        state.hesitationTimer = window.setTimeout(function () {
            if (document.visibilityState === 'hidden') return;

            state.hesitationSent = true;

            trackEvent('ss_pdp_hesitation', Object.assign(pagePayload(), {
                idle_seconds: CONFIG.hesitationSeconds,
                pdp_section: currentSectionLabel(),
                section_index: state.currentSection ? state.currentSection.index : undefined
            }));
        }, CONFIG.hesitationSeconds * 1000);
    }

    /* ------------------------------------------------------------------ exit */

    function fireExit(reason) {
        if (state.exitFired) return;
        state.exitFired = true;

        if (state.hesitationTimer) window.clearTimeout(state.hesitationTimer);

        trackEvent('ss_pdp_exit', Object.assign(pagePayload(), {
            exit_reason: reason,
            exit_to: state.exitTo || 'unknown',
            reached_atc: state.reachedAtc ? 'true' : 'false',
            atc_surface: state.atcSurface || undefined,
            pdp_section: currentSectionLabel(),
            deepest_section: state.deepestSection || undefined,
            section_index: state.deepestSectionIndex || undefined,
            seconds_to_first_action: state.firstActionSeconds === null ? undefined : state.firstActionSeconds,
            scroll_depth_percent: String(state.maxScroll)
        }));
    }

    /* pagehide rather than beforeunload: it fires on bfcache navigations and on
     * mobile tab switches, where beforeunload does not. visibilitychange is a
     * second trigger, not a replacement - iOS Safari has historically dropped
     * pagehide on a backgrounded tab. fireExit is guarded, so whichever arrives
     * first wins and this cannot double-count. */
    window.addEventListener('pagehide', function () { fireExit('page_left'); });

    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') fireExit('page_hidden');
    });

    /* ----------------------------------------------------------------- start */

    /* Guarded because both branches below can fire in the same page life: a
     * script that re-dispatches DOMContentLoaded (PageFly does, on its own soft
     * renders) would otherwise give the page a second ss_pdp_view, a second set
     * of section observers and a doubled section funnel. */
    var started = false;

    function start() {
        if (started) return;
        started = true;

        watchSections();

        window.addEventListener('scroll', onScroll, { passive: true });
        onScroll();

        scheduleHesitation();

        readCart(function (cart) { if (cart) cartCount = cart.item_count; });

        trackEvent('ss_pdp_view', Object.assign(pagePayload(), {
            section_count: sections.length,
            /* Whether the sticky bar and the fake bundle picker exist on this
             * template at all. Without these, an atc_surface split across
             * product pages reads as "nobody uses the sticky bar" when the truth
             * is that it is not there. */
            has_sticky_atc: document.querySelector('product-quick-add, .product-quick-add') ? 'true' : 'false',
            has_bundle_picker: document.querySelector('input[name="product-type-option"]') ? 'true' : 'false'
        }));
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }

    SS.pdpTracking = {
        version: '1.0.0',
        track: trackEvent,
        state: state,
        sections: function () { return sections; },
        config: CONFIG
    };
})();
